import { useState } from 'react';
import { Link } from 'wouter';
import { Mail, CheckCircle2, ArrowRight, Building2, Truck } from 'lucide-react';
import MarketingNav from '../components/marketing/MarketingNav';
import MarketingFooter from '../components/marketing/MarketingFooter';
import { PRICING_CONFIG } from '../config/pricingConfig';
import { usePageSEO } from '../utils/seo';

const ContactPage = () => {
    const [form, setForm] = useState({
        name: '',
        email: '',
        company: '',
        loadsPerWeek: '',
        notes: '',
        website: '', // honeypot
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState<string | null>(null);

    usePageSEO({
        title: 'Contact BayWhiz | Warehouse Dock Scheduling Support & Inquiries',
        description:
            'Contact BayWhiz for product questions, warehouse onboarding assistance, or support at support@baywhiz.com.',
        canonicalPath: '/contact',
    });

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
            setError('Please enter your name, work email, and company name.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch('/api/marketing/demo-requests', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: form.name.trim(),
                    email: form.email.trim(),
                    company: form.company.trim(),
                    loadsPerWeek: form.loadsPerWeek.trim() || undefined,
                    notes: form.notes.trim() || undefined,
                    website: form.website,
                    sourceUrl: window.location.href,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.error || 'Unable to submit your message right now. Please email support@baywhiz.com directly.');
                return;
            }
            setSubmitted(true);
        } catch {
            setError('Network error while sending message. Please email support@baywhiz.com directly.');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-slate-50 text-slate-900 font-sans flex flex-col">
            <MarketingNav variant="light" />

            <main className="flex-1 py-16 px-6">
                <div className="max-w-5xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
                        {/* Left Column: Direct Contact & Self-Serve Paths */}
                        <div className="lg:col-span-5 space-y-6">
                            <div>
                                <span className="inline-flex items-center px-3 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-700 text-xs font-semibold uppercase tracking-wider mb-3">
                                    Contact BayWhiz
                                </span>
                                <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight mb-3">
                                    Get in touch with our team.
                                </h1>
                                <p className="text-base text-slate-600 leading-relaxed">
                                    Have questions about setting up your dock doors, carrier booking links, or subscription plans? Send us a message or email support directly.
                                </p>
                            </div>

                            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-lg bg-teal-50 border border-teal-200 text-teal-700 flex items-center justify-center shrink-0">
                                        <Mail size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold text-slate-900">Email Support &amp; Inquiries</h2>
                                        <p className="text-xs text-slate-500 mt-0.5 mb-1.5">
                                            For warehouse managers, billing questions, and carrier portal support:
                                        </p>
                                        <a
                                            href={`mailto:${PRICING_CONFIG.supportEmail}`}
                                            className="text-sm font-semibold text-teal-700 hover:text-teal-800 underline"
                                        >
                                            {PRICING_CONFIG.supportEmail}
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm space-y-4">
                                <div className="flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                                        <Building2 size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold text-slate-900">Want to test BayWhiz right now?</h2>
                                        <p className="text-xs text-slate-600 mt-1 mb-3 leading-relaxed">
                                            You don&apos;t need to wait for a sales call to try the software. Start a 30-day full-access trial with no credit card required.
                                        </p>
                                        <Link
                                            href="/request-trial"
                                            className="inline-flex items-center gap-1.5 text-xs font-bold text-teal-700 hover:text-teal-800"
                                        >
                                            Request Free Trial <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>

                                <div className="pt-4 border-t border-slate-100 flex items-start gap-3.5">
                                    <div className="w-10 h-10 rounded-lg bg-slate-100 border border-slate-200 text-slate-700 flex items-center justify-center shrink-0">
                                        <Truck size={18} />
                                    </div>
                                    <div>
                                        <h2 className="text-sm font-bold text-slate-900">Are you a freight carrier?</h2>
                                        <p className="text-xs text-slate-600 mt-1 mb-2 leading-relaxed">
                                            To request a dock appointment at a specific warehouse, use the public booking link provided by that facility, or sign in to the Carrier Portal to manage existing bookings.
                                        </p>
                                        <Link
                                            href="/carrier-login"
                                            className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-700 hover:text-slate-900"
                                        >
                                            Go to Carrier Portal <ArrowRight size={14} />
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Inquiry Form */}
                        <div className="lg:col-span-7">
                            <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm">
                                {submitted ? (
                                    <div className="py-8 text-center space-y-4">
                                        <div className="w-12 h-12 rounded-full bg-teal-50 border border-teal-200 text-teal-600 flex items-center justify-center mx-auto">
                                            <CheckCircle2 size={24} />
                                        </div>
                                        <h2 className="text-2xl font-bold text-slate-900">Message Received</h2>
                                        <p className="text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                                            Thank you for reaching out. Your inquiry has been logged and our team will follow up at <strong>{form.email}</strong>. You can also email us anytime at{' '}
                                            <a href={`mailto:${PRICING_CONFIG.supportEmail}`} className="text-teal-700 underline">
                                                {PRICING_CONFIG.supportEmail}
                                            </a>
                                            .
                                        </p>
                                        <div className="pt-4">
                                            <Link
                                                href="/request-trial"
                                                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm"
                                            >
                                                Request Free Trial <ArrowRight size={16} />
                                            </Link>
                                        </div>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-xl font-bold text-slate-900 mb-1">Send us a message</h2>
                                        <p className="text-sm text-slate-500 mb-6">
                                            Tell us about your warehouse operation or question.
                                        </p>

                                        {error && (
                                            <div className="mb-5 p-3.5 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                                                {error}
                                            </div>
                                        )}

                                        <form onSubmit={handleSubmit} className="space-y-4">
                                            {/* Honeypot */}
                                            <div className="hidden" aria-hidden="true">
                                                <label htmlFor="contact-website">Website</label>
                                                <input
                                                    id="contact-website"
                                                    type="text"
                                                    tabIndex={-1}
                                                    autoComplete="off"
                                                    value={form.website}
                                                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                                                />
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label htmlFor="contact-name" className="block text-sm font-medium text-slate-700 mb-1">
                                                        Your Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        id="contact-name"
                                                        type="text"
                                                        required
                                                        value={form.name}
                                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                        placeholder="Jordan Mercer"
                                                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="contact-email" className="block text-sm font-medium text-slate-700 mb-1">
                                                        Work Email <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        id="contact-email"
                                                        type="email"
                                                        required
                                                        value={form.email}
                                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                        placeholder="jordan@warehouse.com"
                                                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    />
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div>
                                                    <label htmlFor="contact-company" className="block text-sm font-medium text-slate-700 mb-1">
                                                        Company / Facility Name <span className="text-red-500">*</span>
                                                    </label>
                                                    <input
                                                        id="contact-company"
                                                        type="text"
                                                        required
                                                        value={form.company}
                                                        onChange={(e) => setForm({ ...form, company: e.target.value })}
                                                        placeholder="Pacific Distribution Center"
                                                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    />
                                                </div>

                                                <div>
                                                    <label htmlFor="contact-loads" className="block text-sm font-medium text-slate-700 mb-1">
                                                        Approx. Weekly Inbound/Outbound Loads
                                                    </label>
                                                    <select
                                                        id="contact-loads"
                                                        value={form.loadsPerWeek}
                                                        onChange={(e) => setForm({ ...form, loadsPerWeek: e.target.value })}
                                                        className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                    >
                                                        <option value="">Select range (optional)</option>
                                                        <option value="1-25">1 – 25 loads / week</option>
                                                        <option value="26-75">26 – 75 loads / week</option>
                                                        <option value="76-200">76 – 200 loads / week</option>
                                                        <option value="200+">200+ loads / week</option>
                                                    </select>
                                                </div>
                                            </div>

                                            <div>
                                                <label htmlFor="contact-notes" className="block text-sm font-medium text-slate-700 mb-1">
                                                    How can we help?
                                                </label>
                                                <textarea
                                                    id="contact-notes"
                                                    rows={4}
                                                    value={form.notes}
                                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                                    placeholder="Tell us about your dock doors, scheduling workflow, or questions..."
                                                    className="w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                                                />
                                            </div>

                                            <button
                                                type="submit"
                                                disabled={submitting}
                                                className="w-full h-12 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm shadow-sm transition-colors disabled:opacity-60"
                                            >
                                                {submitting ? 'Sending Message...' : 'Send Message'}
                                            </button>

                                            <p className="text-xs text-slate-500 text-center">
                                                We only use your inquiry details to respond to your message—never for third-party marketing.
                                            </p>
                                        </form>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            <MarketingFooter variant="light" />
        </div>
    );
};

export default ContactPage;
